import { useMemo, useState } from 'react';
import { ui } from '../ui';
import { colors } from '../theme';

const PAGE_SIZE = 20;

/**
 * Табличный компонент с сортировкой по клику на заголовок и пагинацией —
 * общий для всех списков в админке (Refero-style). columns: [{ key, label,
 * sortable=true, render(row), sortValue(row) }]. Сортировка/страница сбрасываются,
 * когда меняется набор строк (например, из-за фильтра) — так проще, чем гадать,
 * актуальна ли ещё текущая страница.
 */
export default function DataTable({ columns, rows, rowKey, onRowClick, emptyText = 'Ничего не найдено', pageSize = PAGE_SIZE }) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return rows;
    const getVal = col.sortValue || col.render || (row => row[col.key]);
    const copy = [...rows];
    copy.sort((a, b) => {
      let va = getVal(a), vb = getVal(b);
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setPage(0);
    setSort(s => s.key === col.key ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: 'asc' });
  };

  if (rows.length === 0) return <div style={ui.empty}>{emptyText}</div>;

  return (
    <div>
      <table style={ui.table}>
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                style={{ ...ui.th, cursor: col.sortable === false ? 'default' : 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort(col)}
              >
                {col.label}
                {sort.key === col.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageRows.map(row => (
            <tr key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined} style={onRowClick ? { cursor: 'pointer' } : undefined}>
              {columns.map(col => (
                <td key={col.key} style={ui.td}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
          <span style={{ color: colors.textMuted, fontSize: 13 }}>
            Стр. {currentPage + 1} из {totalPages} ({sorted.length})
          </span>
          <button style={ui.btnSecondary} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>← Назад</button>
          <button style={ui.btnSecondary} disabled={currentPage >= totalPages - 1} onClick={() => setPage(currentPage + 1)}>Вперёд →</button>
        </div>
      )}
    </div>
  );
}
